const getZonedMinuteKey = (instant, timeZone) => {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(instant);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${values.year}-${values.month}-${values.day}T${values.hour}:${values.minute}`;
  } catch {
    return '';
  }
};

const zonedWallTimeToInstant = ({ startDate, startTime, timeZone }) => {
  const [year, month, day] = String(startDate || '').split('-').map(Number);
  const [hour, minute] = String(startTime || '').split(':').map(Number);
  if (![year, month, day, hour, minute].every(Number.isFinite)) return null;
  const desiredUtcMs = Date.UTC(year, month - 1, day, hour, minute);
  let candidate = new Date(desiredUtcMs);
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const representedKey = getZonedMinuteKey(candidate, timeZone);
    if (!representedKey) return null;
    const representedUtcMs = new Date(`${representedKey}:00Z`).getTime();
    const difference = desiredUtcMs - representedUtcMs;
    if (difference === 0) return candidate;
    candidate = new Date(candidate.getTime() + difference);
  }
  return getZonedMinuteKey(candidate, timeZone) === `${startDate}T${startTime}` ? candidate : null;
};

export const isUpcomingAppointment = (appointment, now = new Date()) => {
  const currentMinuteKey = getZonedMinuteKey(now, appointment?.timeZone);
  return appointment?.status === 'confirmed'
    && Boolean(currentMinuteKey)
    && `${appointment.startDate || ''}T${appointment.startTime || ''}` >= currentMinuteKey;
};

export const isPastAppointment = (appointment, now = new Date()) => !isUpcomingAppointment(appointment, now);

export const formatAppointmentDateTime = (appointment) => {
  const [year, month, day] = String(appointment?.startDate || '').split('-').map(Number);
  const [hour, minute] = String(appointment?.startTime || '').split(':').map(Number);
  if (![year, month, day, hour, minute].every(Number.isFinite)) return '';
  const displayDate = new Date(Date.UTC(year, month - 1, day, hour, minute));
  const dateLabel = new Intl.DateTimeFormat([], { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' }).format(displayDate);
  const timeLabel = new Intl.DateTimeFormat([], { timeZone: 'UTC', hour: 'numeric', minute: '2-digit' }).format(displayDate);
  try {
    const zoneLabel = new Intl.DateTimeFormat('en-US', {
      timeZone: appointment.timeZone,
      timeZoneName: 'short',
    }).formatToParts(new Date(Date.UTC(year, month - 1, day, 12)))
      .find((part) => part.type === 'timeZoneName')?.value || '';
    return `${dateLabel}, ${timeLabel}${zoneLabel ? ` ${zoneLabel}` : ''}`;
  } catch {
    return 'Date unavailable';
  }
};

export const formatAppointmentDay = (dayKey) => {
  const [year, month, day] = String(dayKey || '').split('-').map(Number);
  if (![year, month, day].every(Number.isFinite)) return '';
  return new Intl.DateTimeFormat([], { timeZone: 'UTC', weekday: 'long', month: 'long', day: 'numeric' })
    .format(new Date(Date.UTC(year, month - 1, day, 12)));
};

export const getAppointmentLeadMinutes = (appointment, now = new Date()) => {
  const instant = zonedWallTimeToInstant(appointment);
  return instant ? (instant.getTime() - now.getTime()) / 60000 : null;
};

export const getZonedDayKey = (instant = new Date(), timeZone) => getZonedMinuteKey(instant, timeZone).slice(0, 10);

export const getAppointmentReminderMinutes = (appointment, enabled, now = new Date()) => {
  if (!enabled) return null;
  const leadMinutes = getAppointmentLeadMinutes(appointment, now);
  if (leadMinutes >= 1440) return 1440;
  if (leadMinutes >= 60) return 60;
  return null;
};

export const groupAppointmentsByDay = (appointments = []) => appointments.reduce((groups, appointment) => {
  groups[appointment.startDate] = [...(groups[appointment.startDate] || []), appointment];
  return groups;
}, {});
